import * as Sentry from '@sentry/react';
import { useLocation } from '@do/seashell/navigation';
import { useEffect, useState } from 'react';
import { fetchRegistrationVariants } from '../utils/fetch-registration-variants';
import { RegistrationVariantProps } from '../interfaces/registrationVariant';
import { GetRegistrationInfo } from '../generated-types/GetRegistrationInfo';
import { registrationVariants as hardcodedVariants } from '../registration-variants';
import { getQueryParam } from '../utils/query-utils';
import { ONBOARDING_ORIGIN_PARAM, REFCODE_PARAM } from '../constants';
import { trackEvent } from '@do/seashell';
import { getRegistrationVariantFromQuery as getRegistrationVariantFromFormData } from '../utils/registration-variant-utils';

interface GetRegistrationVariantProps {
  strapiData: RegistrationVariantProps[];
  onboardingOriginQueryParam: string;
  refcodeQueryParam: string;
}

const getRegistrationVariant = ({
  strapiData,
  onboardingOriginQueryParam,
  refcodeQueryParam,
}: GetRegistrationVariantProps) => {
  const variant = hardcodedVariants.primary;

  const activeStrapiVariants = strapiData.filter((variant) => {
    if (variant.publishDate && variant.removalDate) {
      const currentDate = new Date();
      const publishDate = new Date(variant.publishDate);
      const removalDate = new Date(variant.removalDate);
      return currentDate >= publishDate && currentDate <= removalDate;
    } else {
      return variant;
    }
  });

  // check variants that use query params
  if (onboardingOriginQueryParam) {
    const matchedOnboardingOriginVariant = activeStrapiVariants.find(
      (variant) => variant?.urlParams?.onboarding_origin === onboardingOriginQueryParam,
    );

    if (matchedOnboardingOriginVariant) {
      return matchedOnboardingOriginVariant;
    }
  } else if (refcodeQueryParam) {
    const matchedRefcodeVariant = activeStrapiVariants.find(
      (variant) => variant?.urlParams?.refcode === refcodeQueryParam,
    );
    if (matchedRefcodeVariant) {
      return matchedRefcodeVariant;
    }
  }

  return variant;
};

interface UseRegistrationVariantsProps {
  registrationFormData?: GetRegistrationInfo;
  isLoadingRegistrationFormData: boolean;
  overrideVariant?: RegistrationVariantProps | null;
}

export const useRegistrationVariants = ({
  registrationFormData,
  isLoadingRegistrationFormData,
  overrideVariant,
}: UseRegistrationVariantsProps) => {
  const [registrationVariant, setRegistrationVariant] = useState<RegistrationVariantProps | null>(null);
  const location = useLocation();
  const { search } = location;

  // query params
  const onboardingOriginQueryParam = getQueryParam(ONBOARDING_ORIGIN_PARAM, search) || '';
  const refcodeQueryParam = getQueryParam(REFCODE_PARAM, search) || '';

  useEffect(() => {
    const registrationFormMpAppData = registrationFormData?.registrationFormData?.marketplace_app;
    const registrationFormOauthAppData = registrationFormData?.registrationFormData?.oauth_app;
    const registrationFormDataTeam = registrationFormData?.registrationFormData?.team;

    const shouldShowVariantUsingRegistrationFormData =
      !!registrationFormMpAppData?.name || !!registrationFormOauthAppData?.name || !!registrationFormDataTeam;

    // first check these as they may also use the onboarding_origin query param which for other cases won't use the registrationFormData
    // e.g. MP query param is onboarding_origin = marketplace%3Awordpress, is used for fetching mp field in getRegistrationFormData query
    if (overrideVariant && Object.keys(overrideVariant).length) {
      setRegistrationVariant(overrideVariant);
      return;
    }
    if (!isLoadingRegistrationFormData && shouldShowVariantUsingRegistrationFormData) {
      const formDataVariant = getRegistrationVariantFromFormData(registrationFormData);

      setRegistrationVariant(formDataVariant);
      return;
    }

    const fetchAndFindVariants = async () => {
      try {
        const strapiData = await fetchRegistrationVariants();
        const selectedRegistrationVariant = getRegistrationVariant({
          strapiData,
          onboardingOriginQueryParam,
          refcodeQueryParam,
        });

        setRegistrationVariant(selectedRegistrationVariant);
      } catch (error) {
        const defaultVariant = hardcodedVariants.primary;

        console.error('Unable to load registration variants', error);
        Sentry.captureException(error);
        trackEvent('Registration Variant', { variantType: defaultVariant.type });
        setRegistrationVariant(defaultVariant);
      }
    };

    if (!isLoadingRegistrationFormData) {
      fetchAndFindVariants();
    }
  }, [onboardingOriginQueryParam, refcodeQueryParam, isLoadingRegistrationFormData]); // eslint-disable-line react-hooks/exhaustive-deps

  return { registrationVariant };
};
